import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { FileExtension, ImmomioIconName, isImage, isPdf } from '@resident-nx/shared';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { LoaderWebComponent } from '../loader/loader.component';
import { IconWebComponent } from '../icon/icon.component';
import { FileRequestedInfoComponent } from '../file-requested-info/file-requested-info.component';
import { NgTemplateOutlet } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';

export interface FilePreviewModalFile {
  identifier: string;
  name: string;
  extension?: FileExtension;
  fileUrl?: string | SafeResourceUrl;
  downloadUrl?: string;
  isRequested?: boolean;
  isAlreadyRequested?: boolean;
  isUnavailable?: boolean;
}

export interface FilePreviewModalData {
  files: FilePreviewModalFile[];
  startIndex?: number;
}

@Component({
  selector: 'rs-web-file-preview-modal',
  imports: [LoaderWebComponent, IconWebComponent, FileRequestedInfoComponent, NgTemplateOutlet],
  templateUrl: './file-preview-modal.component.html',
  styleUrl: './file-preview-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewModalComponent {
  protected readonly ImmomioIconName = ImmomioIconName;

  #dialogRef = inject(DialogRef);
  #dialogData: FilePreviewModalData = inject(DIALOG_DATA);

  download = output<FilePreviewModalFile>();

  /**
   * `true` once a preview was actually rendered — not merely requested, unavailable or
   * still loading. Read it from `dialogRef.closed` to react after the preview is gone
   * (ART-19551). It stays readable after the component is destroyed.
   */
  readonly previewWasSeen = signal(false);

  files = signal<FilePreviewModalFile[]>(this.#dialogData.files);
  currentFileIndex = signal(this.#dialogData.startIndex ?? 0);
  currentFile = computed(() => this.files()[this.currentFileIndex()]);
  hasMultipleFiles = computed(() => this.files().length > 1);
  isLoading = computed(() => !this.currentFile()?.fileUrl);

  isCurrentFileRequested = computed(
    () => this.currentFile()?.isRequested || this.currentFile()?.isAlreadyRequested
  );
  isCurrentFileUnavailable = computed(() => this.currentFile()?.isUnavailable);

  isCurrentFileImage = computed(() => isImage(this.currentFile()?.extension));
  isCurrentFilePdf = computed(() => isPdf(this.currentFile()?.extension));

  constructor() {
    const previewWasSeenEff = effect(() => {
      if (!this.isLoading() && !this.isCurrentFileUnavailable() && !this.isCurrentFileRequested()) {
        this.previewWasSeen.set(true);
        previewWasSeenEff.destroy();
      }
    });
  }

  onDownload(): void {
    const file = this.currentFile();
    if (file?.downloadUrl) {
      this.download.emit(file);
    }
  }

  nextFile(): void {
    const isAtLastFile = this.currentFileIndex() === this.files().length - 1;
    if (isAtLastFile) {
      this.currentFileIndex.set(0);
    } else {
      this.currentFileIndex.update(index => index + 1);
    }
  }

  previousFile(): void {
    const isAtFirstFile = this.currentFileIndex() === 0;
    if (isAtFirstFile) {
      this.currentFileIndex.set(this.files().length - 1);
    } else {
      this.currentFileIndex.update(index => index - 1);
    }
  }

  setIndex(index: number): void {
    this.currentFileIndex.set(index);
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }
}
